import { gql, useMutation } from "@apollo/client";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { sortBy, startCase } from "lodash";

import AddRemoveList from "~/components/common/AddRemoveList";
import Help from "~/components/common/Help";
import { orJoin } from "~/utils/visitsHelper";
import ConfigureVisitType from "./ConfigureVisitType";
import VisitResourceRequirementFormRow from "./VisitResourceRequirementFormRow";
import VisitTypeServices from "./VisitTypeServices";
import { useOrgConfig } from "~/providers/OrgConfigProvider";

type VisitTypeProps = {
  visitTypeId: number,
};

const VisitType = ({ visitTypeId }: VisitTypeProps) => {
  const { visitTypes, providerTypes } = useOrgConfig();
  const visitType = visitTypes[visitTypeId];

  const items = visitType?.visitResourceRequirements.map(vrr => ({
      key: vrr.id,
      label: <Accordion sx={{ width: "100%", mr: 2 , "&.Mui-expanded": { mr: 2 }}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {orJoin(vrr.providerTypes.map(pt => startCase(pt.name)).sort())}
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <VisitResourceRequirementFormRow key={vrr.id} visitResourceRequirement={vrr} visitType={visitType} />
        </AccordionDetails>
      </Accordion>,
  }));

  const availableProviderTypes = sortBy(Object.values(providerTypes)
    .map(pt => ({ key: pt.id, label: startCase(pt.name) })), "label");

  const [createVRR, { loading: loadingCreate }] = useMutation(CREATE_VISIT_RESOURCE_REQUIREMENT);
  const onAdd = async (providerTypeIds: number[]) => {
    if (!visitType) { return }
    await createVRR({
      variables: {
        providerTypeIds,
        visitTypeId: visitType.id,
        duration: 30,
      },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  };

  const [deleteVRR, { loading: loadingDelete }] = useMutation(DELETE_VISIT_RESOURCE_REQUIREMENT);

  const onDelete = async (visitResourceRequirementId: number) => {
    await deleteVRR({
      variables: {
        visitResourceRequirementId,
      },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  };

  const minOffset = Math.min(...visitType?.visitResourceRequirements.map(vrr => vrr.offset) || []);

  const invitedVRR = visitType?.visitResourceRequirements.find(vrr => vrr.invitedToVisit);

  if (!visitType) { return null; }
  return <Grid container sx={{ alignItems: "stretch" }} spacing={2}>
    <Grid size={{ xs: 12, xl: 6 }}>
      <ConfigureVisitType visitType={visitType} key={visitType?.id} />
    </Grid>
    <Grid size={{ xs: 12, xl: 6 }}>
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Typography>Providers</Typography>
          <Divider sx={{ mt: 1 }}></Divider>

          {visitType && visitType.visitResourceRequirements.length > 0 && minOffset > 0 && <Paper sx={{ ml: 2, mr: 8, p: 2, borderColor: "warning" }}>
            <WarningAmberRoundedIcon color="warning" sx={{ verticalAlign: "bottom", mr: 0.5 }} />
            All providers have an offset configured; no one will be with the patient for the first {minOffset} minutes of the visit.
          </Paper>}

          {invitedVRR && !visitType?.visitResourceRequirements.find(vrr => vrr.inHome) && <Paper sx={{ ml: 2, mr: 8, p: 2, borderColor: "warning" }}>
            <WarningAmberRoundedIcon color="warning" sx={{ verticalAlign: "bottom", mr: 0.5 }} />
            The {orJoin(invitedVRR.providerTypes.map(pt => startCase(pt.name)).sort())} is marked as "Invited to Visit", but there are no in home providers configured; no one will be able to invite them.
          </Paper>}

          <AddRemoveList
            emptyLabel="No Providers Configured"
            items={items ?? []}
            addOptions={availableProviderTypes}

            addLabel={`Add provider`}
            addMultiple={true}
            onAdd={onAdd}
            addLoading={loadingCreate}

            onRemove={onDelete}
            removeLoading={loadingDelete}
          />
        </CardContent>
      </Card>
    </Grid>
    <Grid size={{ xs: 12, xl: 6 }}>
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Typography>
            Services
            <Help text="Services offered for this visit type. Services marked as default are pre-selected when scheduling." />
          </Typography>
          <Divider sx={{ mt: 1 }}></Divider>

          <VisitTypeServices visitType={visitType} />
        </CardContent>
      </Card>
    </Grid>
  </Grid>
}

export default VisitType;


const CREATE_VISIT_RESOURCE_REQUIREMENT = gql(`
  mutation CreateVisitResourceRequirement(
    $visitTypeId: Int!
    $duration: Int!
    $providerTypeIds: [Int!]!
  )
  @permission(requires: manageOrgConfig)
  {
    createVisitResourceRequirement(
      visitTypeId: $visitTypeId
      duration: $duration
      providerTypeIds: $providerTypeIds
    ) {
      id
    }
  }
`);

const DELETE_VISIT_RESOURCE_REQUIREMENT = gql(`
  mutation DeleteVisitResourceRequirement(
    $visitResourceRequirementId: Int!
  )
  @permission(requires: manageOrgConfig)
  {
    deleteVisitResourceRequirement(
      id: $visitResourceRequirementId
    ) {
      id
      deleted
    }
  }
`);
